import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { selectFinancialCondominium } from '../lib/financialScope';

const FinancialWorkspaceContext = createContext(null);
const currentPeriod = () => {
  const parts = new Intl.DateTimeFormat('en', { timeZone: 'America/Mexico_City', year: 'numeric', month: '2-digit' }).formatToParts(new Date());
  return `${parts.find((item) => item.type === 'year')?.value}-${parts.find((item) => item.type === 'month')?.value}`;
};

function storedScope() {
  try { return JSON.parse(window.localStorage.getItem('ar_finance_scope') || '{}'); }
  catch (_) { return {}; }
}

const resolveValue = (value, current) => typeof value === 'function' ? value(current) : value;

export function FinancialWorkspaceProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token, api, activeCondominioId, setActiveCondominioId, getDefaultCondominioId } = useAuth();
  const saved = useMemo(storedScope, []);
  const initialQuery = useMemo(() => new URLSearchParams(location.search), []);
  const allowedIds = useMemo(() => new Set([
    ...(user?.condominios_ids || []),
    ...(user?.condominios || []).map((item) => item.id),
    ...Object.keys(user?.effective_permissions?.by_condominio || {}),
  ].filter(Boolean)), [user]);
  const allowedKey = useMemo(() => [...allowedIds].sort().join('|'), [allowedIds]);
  const initialExplicitCondo = initialQuery.get('condominio') || initialQuery.get('condominio_id');
  const initialAuthorizedCondo = initialExplicitCondo && (allowedIds.size === 0 || allowedIds.has(initialExplicitCondo));
  const [condominiumId, setCondominiumIdState] = useState(
    (initialAuthorizedCondo && initialExplicitCondo) || activeCondominioId || saved.condominioId || saved.condominiumId || getDefaultCondominioId?.() || '',
  );
  const [period, setPeriodState] = useState(initialQuery.get('periodo') || initialQuery.get('period') || saved.period || currentPeriod());
  const [accountId, setAccountIdState] = useState(initialQuery.get('cuenta') || initialQuery.get('account_id') || saved.accountId || '');
  const [catalog, setCatalog] = useState([]);
  const scopeRef = useRef({ condominiumId, period, accountId });
  scopeRef.current = { condominiumId, period, accountId };

  const writeFinanceUrl = useCallback((scope) => {
    if (!location.pathname.startsWith('/financiero')) return;
    const next = new URLSearchParams(location.search);
    next.delete('condominio_id'); next.delete('period'); next.delete('account_id');
    if (scope.condominiumId) next.set('condominio', scope.condominiumId); else next.delete('condominio');
    if (scope.period) next.set('periodo', scope.period); else next.delete('periodo');
    if (scope.accountId) next.set('cuenta', scope.accountId); else next.delete('cuenta');
    const nextSearch = next.toString();
    if (nextSearch !== new URLSearchParams(location.search).toString()) {
      navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' }, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  const applyScope = useCallback((updates, { updateUrl = true, updateAuth = true } = {}) => {
    const current = scopeRef.current;
    const next = { ...current, ...updates };
    if (updates.condominiumId !== undefined && updates.condominiumId !== current.condominiumId && updates.accountId === undefined) {
      next.accountId = '';
    }
    scopeRef.current = next;
    if (next.condominiumId !== current.condominiumId) setCondominiumIdState(next.condominiumId);
    if (next.period !== current.period) setPeriodState(next.period);
    if (next.accountId !== current.accountId) setAccountIdState(next.accountId);
    if (updateAuth && next.condominiumId && next.condominiumId !== activeCondominioId) setActiveCondominioId?.(next.condominiumId);
    try { window.localStorage.setItem('ar_finance_scope', JSON.stringify({ condominiumId: next.condominiumId, condominioId: next.condominiumId, period: next.period, accountId: next.accountId })); } catch (_) {}
    if (updateUrl) writeFinanceUrl(next);
  }, [activeCondominioId, setActiveCondominioId, writeFinanceUrl]);

  const setCondominiumId = useCallback((value) => {
    const current = scopeRef.current;
    applyScope({ condominiumId: resolveValue(value, current.condominiumId) || '' });
  }, [applyScope]);
  const setPeriod = useCallback((value) => {
    const current = scopeRef.current;
    applyScope({ period: resolveValue(value, current.period) || currentPeriod() });
  }, [applyScope]);
  const setAccountId = useCallback((value) => {
    const current = scopeRef.current;
    applyScope({ accountId: resolveValue(value, current.accountId) || '' });
  }, [applyScope]);

  useEffect(() => {
    if (!token || !user) return;
    const controller = new AbortController();
    api.get('/condominios', { signal: controller.signal })
      .then((response) => {
        const rows = response.data;
        if (Array.isArray(rows)) setCatalog(rows);
      })
      // Conservar el último catálogo válido durante una renovación de sesión o
      // una transición abortada evita borrar el contexto y pintar importes en cero.
      .catch(() => { /* El cliente autenticado resuelve renovación o cierre de sesión. */ });
    return () => controller.abort();
  }, [token, user, api]);

  useEffect(() => {
    // Do not overwrite a remembered finance scope while /auth/me is still loading.
    if (!token || !user || !location.pathname.startsWith('/financiero')) return;
    const query = new URLSearchParams(location.search);
    const explicitCondo = query.get('condominio') || query.get('condominio_id');
    const authorized = explicitCondo && (allowedIds.size === 0 || allowedIds.has(explicitCondo));
    const preferred = getDefaultCondominioId?.();
    const remembered = scopeRef.current.condominiumId || saved.condominioId || saved.condominiumId;
    const fallback = selectFinancialCondominium({ remembered, active: activeCondominioId, preferred, allowedIds });
    applyScope({
      condominiumId: authorized ? explicitCondo : fallback,
      period: query.get('periodo') || query.get('period') || scopeRef.current.period || currentPeriod(),
      accountId: query.get('cuenta') || query.get('account_id') || '',
    }, { updateUrl: !authorized && Boolean(fallback), updateAuth: true });
  }, [location.pathname, location.search, allowedKey, token, user?.id]);

  useEffect(() => {
    if (location.pathname.startsWith('/financiero') || !activeCondominioId) return;
    applyScope({ condominiumId: activeCondominioId }, { updateUrl: false, updateAuth: false });
  }, [activeCondominioId, location.pathname]);

  const condominiumName = useMemo(() => (
    catalog.find((item) => item.id === condominiumId)?.nombre ||
    (user?.condominios || []).find((item) => item.id === condominiumId)?.nombre || ''
  ), [catalog, user, condominiumId]);
  const permissions = user?.effective_permissions?.by_condominio?.[condominiumId]?.modulos?.financiero || {};
  const unifiedFinancialExperience = condominiumName ? true : null;
  const value = useMemo(() => ({
    organizationId: user?.organization_id || user?.organizacion_id || null,
    condominiumId, setCondominiumId, condominiumName,
    period, setPeriod, accountId, setAccountId,
    currency: 'MXN', accountingBasis: 'accrual', activeFeeVersion: null,
    periodStatus: 'open', permissions, catalog,
    isFinalExperience: unifiedFinancialExperience,
  }), [user, condominiumId, setCondominiumId, condominiumName, period, setPeriod, accountId, setAccountId, permissions, catalog, unifiedFinancialExperience]);
  return <FinancialWorkspaceContext.Provider value={value}>{children}</FinancialWorkspaceContext.Provider>;
}

export function useFinancialWorkspace() {
  const value = useContext(FinancialWorkspaceContext);
  if (!value) throw new Error('useFinancialWorkspace requiere FinancialWorkspaceProvider');
  return value;
}
